import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { EVENT, IMAGES } from "@/data/content";
import { Calendar, Clock, MapPin, QrCode, Sparkles } from "lucide-react";

const useCountdown = (target) => {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = new Date(target).getTime() - Date.now();
      if (diff <= 0) return setT({ d: 0, h: 0, m: 0, s: 0 });
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
};

const Unit = ({ v, l }) => (
  <div className="flex flex-col items-center">
    <div className="font-mono-lux text-3xl sm:text-4xl lg:text-5xl gold-text font-700 tabular-nums">
      {String(v).padStart(2, "0")}
    </div>
    <div className="text-[10px] tracking-[0.3em] text-zinc-400 mt-1 uppercase">{l}</div>
  </div>
);

export const Hero = ({ onRegister, onQr }) => {
  const { d, h, m, s } = useCountdown(EVENT.targetDate);

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden grain">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.trophy_hero})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/85 via-[#09090B]/80 to-[#09090B]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#09090B] via-transparent to-[#09090B]/60" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full gold-border bg-amber-500/5 mb-7">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" />
            <span className="font-mono-lux text-[11px] tracking-[0.25em] text-amber-300 uppercase">
              {EVENT.presenters}
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-800 leading-[1.05] text-amber-50">
            <span className="gold-text text-6xl sm:text-8xl lg:text-9xl leading-none align-baseline" data-testid="hero-drop-cap">S</span>OUTH ICON <br />
            <span className="gold-text">AWARDS 2026</span>
          </h1>

          <p className="font-serif-lux text-xl sm:text-2xl text-zinc-300 mt-6 max-w-xl italic">
            {EVENT.tagline}
          </p>

          <div className="flex flex-wrap gap-3 mt-8">
            {[
              { icon: Calendar, text: EVENT.date },
              { icon: Clock, text: EVENT.time },
              { icon: MapPin, text: EVENT.venue },
            ].map((p, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#18181F]/80 gold-border text-sm text-zinc-200"
              >
                <p.icon className="w-4 h-4 text-amber-400" />
                {p.text}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4 mt-10">
            <button
              onClick={onRegister}
              className="gold-gradient-bg text-black font-700 px-8 py-3.5 rounded-full hover:brightness-110 transition-all gold-glow"
              data-testid="hero-nominate-button"
            >
              Nominate Now
            </button>
            <button
              onClick={onQr}
              className="flex items-center gap-2 px-8 py-3.5 rounded-full gold-border text-amber-200 hover:bg-amber-500/10 transition-all"
              data-testid="hero-qr-modal-button"
            >
              <QrCode className="w-5 h-5" /> Scan QR to Register
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-16 inline-flex flex-wrap items-center gap-5 sm:gap-9 px-7 py-5 rounded-2xl bg-[#101014]/70 gold-border backdrop-blur-sm"
          data-testid="hero-countdown"
        >
          <div className="font-serif-lux text-amber-200/80 text-sm pr-2 border-r border-amber-500/20 hidden sm:block">
            The Grand Night<br />Begins In
          </div>
          <Unit v={d} l="Days" />
          <Unit v={h} l="Hours" />
          <Unit v={m} l="Minutes" />
          <Unit v={s} l="Seconds" />
        </motion.div>
      </div>
    </section>
  );
};
